import { trpc } from "@client/trpc";
import { useImpersonationRefValue } from "@client/impersonationRef";
import { buildObjectHash } from "@client/lib/deepLinks/passage";
import { videoStateQueryInput } from "@client/utils/videoStateQueryInput";
import { todoChecklistQueryInput } from "./todoQueryInput";
import {
  useActingAsUserId,
  useEffectiveIsSfcAdmin,
  useRound,
  useUserProfiles,
  useViewerTrack,
} from "../../round-context";
import { useWorkspaceSelectedFunder } from "../useWorkspaceSelectedFunder";
import { getRoundMemberForEvaluator } from "@engine/round/accessors";
import { formatCompactCurrency } from "@sfc/ui";
import { useMemo } from "react";
import {
  funderDecidePath,
  funderReviewPath,
} from "../useFunderWorkspaceRouteState";
import { meetingDeepLink } from "../queue/queueDeepLinks";
import {
  generalRoundNotesLocator,
  recommenderSummaryCardLocator,
} from "../passageLocators";
import { withTodoOverallScoreFocus } from "./todoFocus";
import { assembleTodoRail } from "@lib/todoRail/assembleTodoRail";
import { type TodoRail } from "@lib/todoRail/todoRailModel";

/**
 * Assembles the To Do rail for the round currently in context.
 *
 * Three reads, none of them new corpus traffic: the video catalog the queue
 * already fetches, the watch-progress state the player already fetches, and
 * the rail's own small checklist row. Everything else — recommenders, the
 * funder's ratings, their allocations — comes from the round aggregate the
 * page already holds.
 *
 * The derivation itself lives in `assembleTodoRail` (shared with the server's
 * admin read of a funder's drawer); this hook only wires the workspace's
 * queries and link builders into it.
 */
export function useTodoRail(
  roundId: string,
  enabled: boolean,
): {
  rail: TodoRail | null;
  isLoading: boolean;
  isError: boolean;
} {
  const round = useRound();
  const actingAsUserId = useActingAsUserId();
  // The viewer's track, from the state context — never a `round.members`
  // lookup, which a non-member SFC admin has no entry in. Needed because
  // whether a recommender's notes are READABLE is part of whether the rail may
  // ask for them; see the notes step below.
  const viewerTrack = useViewerTrack();
  const userProfiles = useUserProfiles();
  // The impersonation target (null when nobody is being impersonated) — the
  // cache-key discriminator, which is NOT the same value as actingAsUserId.
  const viewAsUserId = useImpersonationRefValue();
  // The funder whose workspace is on screen — NOT necessarily the viewer. An
  // SFC admin is usually not a funder and picks whose round they're looking at,
  // and reading the viewer's own record instead left every funder-derived step
  // permanently empty for them. Shared with the workspace so they can't differ.
  const { selectedFunder } = useWorkspaceSelectedFunder({
    round,
    actingAsUserId,
    effectiveIsSfcAdmin: useEffectiveIsSfcAdmin(),
  });

  const videosQuery = trpc.getVideos.useQuery(
    { roundId },
    { enabled, staleTime: 60_000 },
  );
  // MUST be the shared key, not `actingAsUserId` — see videoStateQueryInput.
  // This is what makes the rail's percentages move as the player writes.
  const videoStateQuery = trpc.getUserVideoState.useQuery(
    videoStateQueryInput(roundId, viewAsUserId),
    { enabled },
  );
  const checklistQuery = trpc.getRoundChecklist.useQuery(
    todoChecklistQueryInput(roundId, viewAsUserId),
    { enabled },
  );

  const rail = useMemo(() => {
    // EVERY read is required, and none of them has a safe default. An absent
    // progress list is not "nothing watched" and an absent checklist is not
    // "nothing done" — building from either would show 0%, offer finished work
    // as "Next on the list", and (for the checklist) drop every step whose
    // existence depends on it. This is why the guard is on the DATA rather than
    // on `isLoading`: a failed fetch with an empty cache also reports "not
    // loading", and that is precisely when the empty defaults look like fact.
    const videoRows = videosQuery.data;
    const videoState = videoStateQuery.data;
    const checklist = checklistQuery.data;

    if (videoRows == null || videoState == null || checklist == null) {
      return null;
    }

    return assembleTodoRail({
      round,
      roundId,
      funder: selectedFunder,
      viewerTrack,
      viewerUserId: actingAsUserId,
      userProfiles,
      videos: videoRows,
      progress: videoState.progress,
      checklist,
      links: {
        linkToVideo: (video) =>
          meetingDeepLink(
            roundId,
            video.id,
            video.track,
            video.type,
            video.homeRecommenderId,
          ),
        linkToRecommenderNotes: (recommenderId) => {
          const member = getRoundMemberForEvaluator(round, recommenderId);
          const path = funderReviewPath(roundId, "recs", recommenderId);

          if (member == null) {
            return path;
          }

          const locator = generalRoundNotesLocator(
            roundId,
            recommenderId,
            member.id,
          );
          return `${path}${buildObjectHash(locator.scopeId)}`;
        },
        linkToRecommenderProfile: (recommenderId) => {
          const member = getRoundMemberForEvaluator(round, recommenderId);
          const path = funderReviewPath(roundId, "recs", recommenderId);

          if (member == null) {
            return path;
          }

          const locator = recommenderSummaryCardLocator(
            roundId,
            recommenderId,
            member.id,
          );
          return `${path}${buildObjectHash(locator.scopeId)}`;
        },
        linkToRecommenderScore: (recommenderId) =>
          withTodoOverallScoreFocus(
            funderReviewPath(roundId, "recs", recommenderId),
          ),
        // Straight to the "Suggested next" list, not the whole grid. The rail's
        // scoring step asks for a SAMPLE — thirty notes out of hundreds of
        // rateable pairs — and the suggested list is the app's own answer to
        // which thirty. Landing on the unfiltered grid handed that choice back
        // to the funder, which is the work the step exists to save them. The
        // filter is URL-backed for exactly this (see DecideRatings'
        // STATUS_FILTER_PARAM).
        linkToScoring: () =>
          `${funderDecidePath(roundId, "note-ratings")}?status=suggested`,
        linkToEvaluations: () => funderDecidePath(roundId, "incremental-evals"),
      },
      formatCurrency: formatCompactCurrency,
    });
  }, [
    videosQuery.data,
    videoStateQuery.data,
    checklistQuery.data,
    round,
    selectedFunder,
    userProfiles,
    roundId,
    viewerTrack,
    actingAsUserId,
  ]);

  const isError =
    videosQuery.isError || videoStateQuery.isError || checklistQuery.isError;

  // No rail and no error is the only honest "still loading" — the memo above
  // owns readiness, so this can't drift from what it actually requires.
  return { rail, isLoading: rail == null && !isError, isError };
}
